import fetcher from '../../helpers/fetcher';
import { ENVIRONMENT_CONFIG } from '../../constants/environmentConfig';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { unwrapDataResponse } from '../../utils/unwrapDataResponse';
import { indexedDBManager } from '../../utils/indexedDB';
import type {
  Basket,
  BasketQueryParams,
  BasketResponse,
  CreateBasketDto,
  UpdateBasketDto,
} from '../../types/basket';

type BasketListPayload = Partial<Omit<BasketResponse, 'data'>> & {
  data?: Basket[];
};

class BasketService {
  private consumerPriceUrl = ENVIRONMENT_CONFIG.CONSUMER_PRICE_SERVICE;
  private readonly entity = 'basket';

  private normalizeBasket(basket: Basket): Basket {
    const productCount =
      basket.productCount ??
      (basket.mainProducts ?? []).reduce(
        (count, mainProduct) => count + (mainProduct.products?.length ?? 0),
        0
      );

    return {
      ...basket,
      productCount,
    };
  }

  private normalizeBasketResponse(responseData: unknown): BasketResponse {
    const payload = unwrapDataResponse<Basket[] | BasketListPayload>(responseData);
    const payloadObject = !Array.isArray(payload) && payload ? payload : undefined;
    const baskets = Array.isArray(payload)
      ? payload
      : Array.isArray(payloadObject?.data)
        ? payloadObject.data
        : [];

    return {
      data: baskets.map((basket: Basket) => this.normalizeBasket(basket)),
      total: Number(payloadObject?.total ?? baskets.length),
      page: Number(payloadObject?.page ?? 1),
      limit: Number(payloadObject?.limit ?? baskets.length),
      totalPages: Number(payloadObject?.totalPages ?? 1),
    };
  }

  async getBaskets(params: BasketQueryParams = {}): Promise<BasketResponse> {
    const queryParams: Record<string, string | number | number[]> = {};

    if (params.page) queryParams.page = params.page;
    if (params.limit) queryParams.limit = params.limit;
    if (params.sortBy) queryParams.sortBy = params.sortBy;
    if (params.sortOrder) queryParams.sortOrder = params.sortOrder;
    if (params.search) queryParams.search = params.search;
    if (params.mainProductIds?.length) queryParams.mainProductIds = params.mainProductIds;
    if (params.status) queryParams.status = params.status;

    const response = await fetcher.get(
      API_ENDPOINTS.BASKET.GET_BASKET_LIST,
      queryParams,
      undefined,
      this.consumerPriceUrl
    );
    return this.normalizeBasketResponse(response.data);
  }

  async createBasket(basket: CreateBasketDto): Promise<Basket> {
    const response = await fetcher.post(
      API_ENDPOINTS.BASKET.BASKETS,
      basket,
      undefined,
      this.consumerPriceUrl
    );
    return unwrapDataResponse<Basket>(response.data);
  }

  async updateBasket(id: number, basket: UpdateBasketDto): Promise<Basket> {
    const response = await fetcher.patch(
      `${API_ENDPOINTS.BASKET.UPDATE_BASKET}/${id}`,
      basket,
      undefined,
      this.consumerPriceUrl
    );
    return unwrapDataResponse<Basket>(response.data);
  }

  async deleteBasket(id: number): Promise<boolean> {
    const response = await fetcher.del(
      `${API_ENDPOINTS.BASKET.DELETE_BASKET}/${id}`,
      undefined,
      undefined,
      this.consumerPriceUrl
    );
    const isDeleted = response.statusText === 'SUCCESS';

    if (isDeleted) {
      await indexedDBManager.updateLocalData(this.entity, String(id), {
        isActive: false,
        updateTime: Date.now(),
      });
    }

    return isDeleted;
  }

  async restoreBasket(id: number): Promise<boolean> {
    const response = await fetcher.patch(
      `${API_ENDPOINTS.BASKET.RESTORE_BASKET}/${id}/restore`,
      undefined,
      undefined,
      this.consumerPriceUrl
    );
    const isRestored = response.statusText === 'SUCCESS';
    if (isRestored) {
      await indexedDBManager.updateLocalData(this.entity, String(id), {
        isActive: true,
        updateTime: Date.now(),
      });
    }
    return isRestored;
  }
}

export const basketService = new BasketService();
